import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';

interface BrandSetupErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandId?: string;
  errorData?: {
    code: string;
    message: string;
    requiredFields?: Record<string, string>;
  };
}

export const BrandSetupErrorModal: React.FC<BrandSetupErrorModalProps> = ({
  isOpen,
  onClose,
  brandId,
  errorData
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  // Extract required fields for display
  const requiredFields = errorData?.requiredFields || {};
  const fieldEntries = Object.entries(requiredFields);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl border border-black shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              <h2 className="text-xl font-semibold">
                {t('personas.errors.brandSetupIncompleteTitle', 'Configuration de marque incomplète')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-gray-700">
              {t('personas.errors.brandSetupIncompleteMessage', 
                'Les champs suivants sont manquants dans votre marque. Veuillez les compléter pour pouvoir générer des personas pertinents :')}
            </p>
            
            {fieldEntries.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-red-800 mb-2">
                  {t('personas.errors.missingFields', 'Champs manquants :')}
                </h4>
                <ul className="list-disc list-inside space-y-1">
                  {fieldEntries.map(([field, description]) => (
                    <li key={field} className="text-sm text-red-700">
                      <span className="font-medium">
                        {getFieldDisplayName(field, t)}
                      </span>
                      {description && description !== getFieldDisplayName(field, t) && (
                        <span className="text-red-600 ml-1">
                          - {description}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-sm text-gray-600">
              {t('personas.errors.brandSetupInstructions', 
                'Rendez-vous dans la section "Stratégie de marque" pour compléter ces informations.')}
            </p>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
            >
              {t('common.close', 'Fermer')}
            </button>
            <button
              onClick={() => {
                // Navigate to brand edit page if brandId is available, otherwise to brand strategy page
                if (brandId) {
                  window.location.href = `/brand-strategy-lab/${brandId}/edit`;
                } else {
                  window.location.href = '/brand-strategy-lab';
                }
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              {t('personas.errors.goToBrandSetup', 'Aller à la configuration')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get user-friendly field names
function getFieldDisplayName(field: string, t: any): string {
  const fieldMap: Record<string, string> = {
    'brandIdentity.name': t('brands.form.name', 'Nom de la marque'),
    'brandIdentity.industry': t('brands.form.industry', 'Secteur d\'activité'),
    'goToMarketStrategy.marketScope': t('brands.form.marketScope', 'Portée du marché'),
    'goToMarketStrategy.targetMarkets': t('brands.form.targetMarkets', 'Marchés cibles'),
    'goToMarketStrategy.businessModel': t('brands.form.businessModel', 'Modèle économique'),
    'goToMarketStrategy.distributionType': t('brands.form.distributionType', 'Type de distribution'),
    'goToMarketStrategy.targetSegments': t('brands.form.targetSegments', 'Segments cibles'),
    'goToMarketStrategy.keyPersonaTypes': t('brands.form.keyPersonaTypes', 'Types de personas clés'),
  };

  return fieldMap[field] || field.split('.').pop() || field;
} 